import React from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { ArrowDownIcon, BriefcaseIcon, UserRoundIcon } from 'lucide-react';
import { PageHero } from '../components/PageHero';
import { InquiryForm } from '../components/InquiryForm';
import { consultants } from '../data/consultants';
import { isFeatureEnabled } from '../utils/features';
import type { InquiryType } from '../types/content';
const VALID_TYPES: InquiryType[] = ['employer', 'candidate', 'general', 'media'];
const pathways = [{
  type: 'employer' as InquiryType,
  label: 'For Employers',
  title: 'Discuss Your Hiring Needs',
  description: 'Board, CEO, C-suite, or senior leadership search, assessment, and succession advisory.',
  icon: BriefcaseIcon
}, {
  type: 'candidate' as InquiryType,
  label: 'For Candidates',
  title: 'Introduce Yourself',
  description: 'Share your background in confidence so we can consider you for current and future mandates.',
  icon: UserRoundIcon
}];
export function Contact() {
  const [params] = useSearchParams();
  const rawType = params.get('type') as InquiryType | null;
  const selectedType: InquiryType | '' = rawType && VALID_TYPES.includes(rawType) ? rawType : '';
  const role = params.get('role');
  const initialMessage = role ? `I'd like to express interest in the ${role} opportunity.` : '';
  return <>
      <PageHero breadcrumb="Contact Us" title="Begin a confidential conversation." lead="Choose the pathway that fits, or send a general inquiry. Every message is read by our team and handled in strict confidence." data-id="element-255" data-mp="mgkqss2qdgsl" />

      <section className="bg-ivory" data-id="element-256" data-mp="vghkx97w37v1">
        <div className="mx-auto max-w-site px-6 pt-16 lg:px-10 lg:pt-20" data-id="element-257" data-mp="vjlsmgkkclqg">
          <ul className="grid gap-6 md:grid-cols-2" data-id="element-258" data-mp="10hnd95xn0t41">
            {pathways.map(p => {
            const active = selectedType === p.type;
            const Icon = p.icon;
            return <li key={p.type} data-id="element-259" data-mp="2bto7v1h6nbrp">
                  <Link to={`/contact?type=${p.type}#inquiry-form`} replace aria-current={active ? 'true' : undefined} className={`group flex h-full flex-col rounded-lg p-8 transition-colors duration-150 ease-out lg:p-10 ${active ? 'bg-midnight-900 text-champagne-50' : 'bg-white text-midnight-900 ring-1 ring-midnight-900/10 hover:ring-midnight-900/30'}`} data-id="element-260" data-mp="1m73k5ltz5083">
                    <div className="flex items-center gap-3" data-id="element-261" data-mp="yi16ep2gnriv">
                      <Icon className={`h-5 w-5 ${active ? 'text-champagne-300' : 'text-champagne-600'}`} aria-hidden="true" data-id="element-262" data-mp="yfodxhz2tyrk" />
                      <span className={`text-sm font-medium ${active ? 'text-champagne-300' : 'text-champagne-600'}`} data-id="element-263" data-mp="18fypky5gqy4m">{p.label}</span>
                    </div>
                    <h2 className="mt-5 font-serif text-3xl md:text-4xl" data-id="element-264" data-mp="kotail7ck9vd">{p.title}</h2>
                    <p className={`mt-3 text-[15px] leading-relaxed ${active ? 'text-champagne-100/70' : 'text-midnight-900/70'}`} data-id="element-265" data-mp="fqi4klk6ajif">{p.description}</p>
                    <span className={`mt-auto inline-flex items-center gap-2 pt-8 text-sm font-medium ${active ? 'text-champagne-200' : 'text-midnight-900'}`} data-id="element-266" data-mp="2w5d39dy601m4">
                      {active ? 'Selected below' : 'Start here'}
                      <ArrowDownIcon className="h-4 w-4 transition-transform duration-150 ease-out group-hover:translate-y-0.5" aria-hidden="true" data-id="element-267" data-mp="2mes8y2pixj90" />
                    </span>
                  </Link>
                </li>;
          })}
          </ul>
        </div>

        <div id="inquiry-form" className="mx-auto grid max-w-site scroll-mt-36 gap-12 px-6 py-16 lg:grid-cols-12 lg:px-10 lg:py-24" data-id="element-268" data-mp="314q0msmnwr2y">
          <div className="lg:col-span-4" data-id="element-269" data-mp="3550869q3rpjx">
            <h2 className="font-serif text-4xl text-midnight-900" data-id="element-270" data-mp="357w45sie445q">Send an inquiry</h2>
            <p className="mt-4 text-[16px] leading-relaxed text-midnight-900/70" data-id="element-271" data-mp="1pnzpqw7anb30">
              Tell us a little about yourself and what you’d like to discuss. We typically respond within two business days.
            </p>
            <dl className="mt-10 space-y-6 border-t border-midnight-900/15 pt-8 text-[15px]" data-id="element-272" data-mp="257arevhvtew1">
              <div data-id="element-273" data-mp="255hh96828cja">
                <dt className="font-medium text-midnight-900" data-id="element-274" data-mp="207y2m57g9xqt">Confidentiality</dt>
                <dd className="mt-1 leading-relaxed text-midnight-900/70" data-id="element-275" data-mp="1v94r2ibq4gta">Nothing you share is passed to a client or third party without your consent.</dd>
              </div>
              <div data-id="element-276" data-mp="2sw3owwe5jusr">
                <dt className="font-medium text-midnight-900" data-id="element-277" data-mp="2suit73g3q5tb">What happens next</dt>
                <dd className="mt-1 leading-relaxed text-midnight-900/70" data-id="element-278" data-mp="2nvpfb9wwkcsm">A senior member of our team reviews your inquiry and replies personally.</dd>
              </div>
              {isFeatureEnabled('consultants') && <div data-id="element-279" data-mp="17jk2njwswrcm">
                  <dt className="font-medium text-midnight-900" data-id="element-280" data-mp="17jsjfmw1ogt3">Reach our team directly</dt>
                  <dd className="mt-2" data-id="element-281" data-mp="13hfe2zl7mkwu">
                    <ul className="space-y-2" data-id="element-282" data-mp="13gdto4ib0m2g">
                      {consultants.map(c => <li key={c.slug} className="leading-relaxed text-midnight-900/70" data-id="element-283" data-mp="3f6vdwmblvm0n">
                          <span className="font-medium text-midnight-900" data-id="element-284" data-mp="3j7f93kcmcfk5">{c.name}</span>
                          <br data-id="element-285" data-mp="3o56zub1j1vmr" />
                          <a href={`mailto:${c.email}`} className="break-all underline decoration-champagne-500 underline-offset-4 hover:text-midnight-900" data-id="element-286" data-mp="3o3vw2b830nvn">
                            {c.email}
                          </a>
                        </li>)}
                    </ul>
                  </dd>
                </div>}
            </dl>
          </div>
          <div className="lg:col-span-8" data-id="element-287" data-mp="arvi6rzf7989">
            <InquiryForm initialType={selectedType} initialMessage={initialMessage} data-id="element-288" data-mp="fpx55nzyvsp0" />
          </div>
        </div>
      </section>
    </>;
}